import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "@/components/Sidebar";
import PrivacyNotice from "@/components/PrivacyNotice";
import { api } from "@/lib/api";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import {
  Download, X, TrendingUp, Receipt, FileText, Percent, Wallet, AlertCircle, Filter,
} from "lucide-react";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const COLORS = ["#16A34A", "#0EA5E9", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];
const SERVICE_TYPES = ["flight", "hotel", "domestic", "international"];
const BOOKING_STATUS = ["draft", "sent", "accepted", "rejected", "pending", "paid", "cancelled"];

const empty = { date_from: "", date_to: "", destination: "", service_type: "", status: "", customer: "" };

export default function Reports() {
  const [f, setF] = useState(empty);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports", { params: f });
      setD(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f]);

  const exportCSV = (rows, filename) => {
    if (!rows?.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const cards = useMemo(() => d ? [
    { key: "total_revenue", icon: TrendingUp, label: "Total Revenue", value: rupees(d.cards.total_revenue), tint: "emerald" },
    { key: "total_bookings", icon: Receipt, label: "Total Bookings", value: d.cards.total_bookings, tint: "blue" },
    { key: "total_quotations", icon: FileText, label: "Total Quotations", value: d.cards.total_quotations, tint: "purple" },
    { key: "conversion_rate", icon: Percent, label: "Conversion Rate", value: `${d.cards.conversion_rate}%`, tint: "amber" },
    { key: "total_received", icon: Wallet, label: "Amount Received", value: rupees(d.cards.total_received), tint: "emerald" },
    { key: "outstanding", icon: AlertCircle, label: "Outstanding", value: rupees(d.cards.outstanding), tint: "red" },
  ] : [], [d]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar />
      <main className="flex-1">
        <header className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
            <div className="overline text-[10px] tracking-widest text-slate-500">ANALYTICS</div>
            <h1 className="font-editorial text-3xl mt-1">Reports</h1>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          {/* Filters */}
          <div className="bg-white border border-border rounded p-4 mb-6">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-slate-500">
              <Filter size={12} /> Filters
              {Object.values(f).some(Boolean) && (
                <button data-testid="clear-filters" onClick={() => setF(empty)}
                        className="ml-auto text-emerald-700 hover:underline inline-flex items-center gap-1 normal-case tracking-normal text-[11px]">
                  <X size={11} /> Clear
                </button>
              )}
            </div>
            {/* Active-filter chips */}
            {(f.customer || f.destination || f.service_type || f.status) && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {["customer", "destination", "service_type", "status"].map((k) => f[k] && (
                  <span key={k} data-testid={`chip-${k}`}
                        className="inline-flex items-center gap-1.5 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-2.5 py-0.5">
                    <span className="text-emerald-600/70">{k.replace("_", " ")}:</span> {f[k]}
                    <button onClick={() => setF((x) => ({ ...x, [k]: "" }))} className="hover:text-red-600"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <FInput label="From" type="date" value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} testid="f-from" />
              <FInput label="To" type="date" value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testid="f-to" />
              <FInput label="Destination" value={f.destination} onChange={(v) => setF({ ...f, destination: v })} testid="f-dest" placeholder="e.g. UAE" />
              <FSelect label="Service Type" value={f.service_type} onChange={(v) => setF({ ...f, service_type: v })}
                       options={SERVICE_TYPES} testid="f-service" />
              <FSelect label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })}
                       options={BOOKING_STATUS} testid="f-status" />
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {loading && !d && <div className="col-span-full text-center py-8 text-slate-400">Loading…</div>}
            {cards.map((c) => {
              const delta = d?.cards_delta?.[c.key];
              const up = delta != null && delta > 0;
              const down = delta != null && delta < 0;
              // "Outstanding" is inverted — down is good.
              const good = (c.key === "outstanding") ? down : up;
              return (
                <div key={c.label} className="bg-white border border-border rounded p-4">
                  <div className={`w-8 h-8 rounded flex items-center justify-center mb-3 bg-${c.tint}-50 text-${c.tint}-600`}><c.icon size={16} /></div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="mt-1 font-editorial text-slate-900 text-xl">{c.value}</div>
                  {delta != null && (
                    <div data-testid={`delta-${c.key}`} className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${good ? "text-emerald-600" : (delta === 0 ? "text-slate-400" : "text-red-500")}`}>
                      {up ? "▲" : down ? "▼" : "·"} {Math.abs(delta)}% <span className="text-slate-400 font-normal">vs prev</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!d ? null : (
            <>
              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <ChartCard title="Monthly Revenue">
                  {d.sales.revenue_by_month.length === 0 ? <Empty label="No revenue in this range" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={d.sales.revenue_by_month}>
                        <XAxis dataKey="month" fontSize={11} />
                        <YAxis fontSize={11} />
                        <Tooltip formatter={(v) => rupees(v)} />
                        <Line type="monotone" dataKey="value" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
                <ChartCard title="Bookings by Service">
                  {d.bookings.by_service.length === 0 ? <Empty label="No bookings yet" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={d.bookings.by_service} dataKey="count" nameKey="service" outerRadius={80} label>
                          {d.bookings.by_service.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
                <ChartCard title="Revenue by Destination">
                  {d.sales.revenue_by_destination.length === 0 ? <Empty label="No destination data" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={d.sales.revenue_by_destination}>
                        <XAxis dataKey="destination" fontSize={11} />
                        <YAxis fontSize={11} />
                        <Tooltip formatter={(v) => rupees(v)} />
                        <Bar dataKey="value" fill="#0EA5E9" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
                <ChartCard title="Quotation Status">
                  {Object.keys(d.quotations.by_status).length === 0 ? <Empty label="No quotations in range" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={Object.entries(d.quotations.by_status).map(([k, v]) => ({ status: k, value: v }))}
                             dataKey="value" nameKey="status" outerRadius={80} label>
                          {Object.entries(d.quotations.by_status).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              </div>

              {/* Tables */}
              <TableCard title="Revenue by Customer (Top 10)" rows={d.sales.revenue_by_customer}
                cols={[["customer", "Customer"], ["value", "Revenue", rupees]]}
                onRowClick={(r) => setF((x) => ({ ...x, customer: r.customer }))}
                onExport={() => exportCSV(d.sales.revenue_by_customer, "revenue-by-customer.csv")} />

              <TableCard title="Revenue by Destination (Top 10)" rows={d.sales.revenue_by_destination}
                cols={[["destination", "Destination"], ["value", "Revenue", rupees]]}
                onRowClick={(r) => setF((x) => ({ ...x, destination: r.destination }))}
                onExport={() => exportCSV(d.sales.revenue_by_destination, "revenue-by-destination.csv")} />

              <TableCard title="Revenue by Service Type" rows={d.sales.revenue_by_service}
                cols={[["service", "Service"], ["value", "Revenue", rupees]]}
                onRowClick={(r) => setF((x) => ({ ...x, service_type: r.service }))}
                onExport={() => exportCSV(d.sales.revenue_by_service, "revenue-by-service.csv")} />

              <TableCard title="Service Performance" rows={d.services.by_service}
                cols={[["service", "Service"], ["bookings", "Bookings"], ["revenue", "Revenue", rupees]]}
                onRowClick={(r) => setF((x) => ({ ...x, service_type: r.service }))}
                onExport={() => exportCSV(d.services.by_service, "service-performance.csv")} />

              <TableCard title="Outstanding Invoices" rows={d.payments.invoice_outstanding}
                cols={[["invoice_number", "Invoice"], ["customer", "Customer"], ["grand_total", "Total", rupees],
                       ["received", "Received", rupees], ["balance", "Balance", rupees], ["status", "Status"]]}
                onExport={() => exportCSV(d.payments.invoice_outstanding, "outstanding-invoices.csv")}
                empty="No outstanding invoices" />

              {/* Top 10 Destinations — Previous calendar month */}
              {d.top_destinations && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <TopDestinations
                    title={`Top 10 Domestic Destinations · ${d.top_destinations.period}`}
                    rows={d.top_destinations.domestic}
                    onDrill={(dest) => setF((x) => ({ ...x, destination: dest }))}
                    testidPrefix="top-domestic"
                    color="#16A34A"
                  />
                  <TopDestinations
                    title={`Top 10 International Destinations · ${d.top_destinations.period}`}
                    rows={d.top_destinations.international}
                    onDrill={(dest) => setF((x) => ({ ...x, destination: dest }))}
                    testidPrefix="top-international"
                    color="#0EA5E9"
                  />
                </div>
              )}              {/* Customer + Quotation summaries side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <ChartCard title="Customer Snapshot">
                  <div className="grid grid-cols-2 gap-4 text-sm p-2">
                    <MiniStat label="Total Customers" value={d.customers.total} />
                    <MiniStat label="New This Month" value={d.customers.new_this_month} />
                    <MiniStat label="Individual" value={d.customers.individual} />
                    <MiniStat label="Corporate" value={d.customers.corporate} />
                    <MiniStat label="Repeat Customers" value={d.customers.repeat} />
                  </div>
                </ChartCard>
                <ChartCard title="Quotation Snapshot">
                  <div className="grid grid-cols-2 gap-4 text-sm p-2">
                    <MiniStat label="Total" value={d.quotations.total} />
                    <MiniStat label="Accepted" value={d.quotations.accepted} />
                    <MiniStat label="Value" value={rupees(d.quotations.value)} />
                    <MiniStat label="Conversion" value={`${d.quotations.conversion_rate}%`} />
                  </div>
                </ChartCard>
              </div>
            </>
          )}

          <PrivacyNotice />
        </div>
      </main>
    </div>
  );
}

function FInput({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full border border-border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
    </div>
  );
}
function FSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full border border-border rounded px-2.5 py-1.5 text-sm bg-white">
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-border rounded p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}
function Empty({ label }) { return <div className="h-[240px] flex items-center justify-center text-xs text-slate-400">{label}</div>; }
function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-editorial text-xl mt-0.5">{value}</div>
    </div>
  );
}
function TableCard({ title, rows, cols, onExport, onRowClick, empty = "No data" }) {
  return (
    <div className="bg-white border border-border rounded overflow-hidden mb-4">
      <div className="px-6 py-3 border-b border-border flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
        {rows?.length > 0 && (
          <button onClick={onExport} data-testid={`export-${title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1">
            <Download size={11} /> Export CSV
          </button>
        )}
      </div>
      {!rows?.length ? <div className="px-6 py-8 text-xs text-slate-400 text-center">{empty}</div> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{cols.map(([, l]) => <th key={l} className="text-left px-6 py-3">{l}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={onRowClick ? () => onRowClick(r) : undefined}
                  data-testid={onRowClick ? `drill-${title.replace(/\s+/g, "-").toLowerCase()}-${i}` : undefined}
                  className={`border-t border-border ${onRowClick ? "cursor-pointer hover:bg-emerald-50/50" : ""}`}>
                {cols.map(([k, , fmt]) => <td key={k} className="px-6 py-2.5">{fmt ? fmt(r[k]) : r[k] ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TopDestinations({ title, rows, onDrill, testidPrefix, color }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.bookings));
  return (
    <div className="bg-white border border-border rounded overflow-hidden">
      <div className="px-6 py-3 border-b border-border">
        <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
      </div>
      {!rows?.length ? (
        <div className="px-6 py-8 text-xs text-slate-400 text-center">No confirmed bookings last month</div>
      ) : (
        <div className="p-4 space-y-2">
          {rows.map((r) => (
            <button key={r.rank} data-testid={`${testidPrefix}-${r.rank}`}
                    onClick={() => onDrill(r.destination)}
                    className="w-full text-left group flex items-center gap-3 hover:bg-emerald-50/50 rounded px-2 py-1.5">
              <span className="w-6 text-xs font-mono-e text-slate-400 shrink-0">#{r.rank}</span>
              <span className="w-32 text-sm text-slate-800 truncate shrink-0">{r.destination}</span>
              <span className="flex-1 h-2.5 bg-slate-100 rounded relative overflow-hidden">
                <span className="absolute inset-y-0 left-0 rounded transition-all"
                      style={{ width: `${(r.bookings / max) * 100}%`, background: color }} />
              </span>
              <span className="w-10 text-right text-sm font-medium text-slate-800 shrink-0">{r.bookings}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

